import { env } from '@/env';
import { BASE_TURBOPRO } from './base';
import { anunciarSessaoExpirada } from './sessao-expirada';
import { executarComTrava } from './trava-refresh';
import type { ApiResponse, Unwrapped } from './envelope';
import { HttpError, unwrap, unwrapLista } from './envelope';

/**
 * Client único da API do TurboPro (ItWorks.Api) — substitui `apiIC`/
 * `apiEmalote`/`apiImpostoGrama` e os Bearer tokens fixos de
 * `src/services/index.ts`.
 *
 * - O access token (JWT) vive SÓ EM MEMÓRIA (nunca em localStorage).
 * - O refresh token vive em cookie HttpOnly do domínio da API — o front nunca
 *   o lê, só garante `credentials: 'include'` em toda chamada.
 * - O refresh é ROTATIVO: se duas chamadas concorrentes perderem o token e
 *   cada uma disparar seu próprio refresh, a primeira rotaciona o cookie e a
 *   segunda chega com refresh já inválido. Por isso o SINGLE-FLIGHT: a
 *   primeira falha renova, as demais esperam a MESMA promise e repetem com o
 *   token novo.
 * - `ApuracaoCFOP`, `ApuracaoCBS`, `IntegracoesRTC` e `SimplesNacional` ainda
 *   são atendidos pelo backend legado através do gateway da ItWorks.Api, e
 *   esse legado não usa o status HTTP como contrato de autenticação: bearer
 *   ausente/expirado volta como HTTP 400 com uma mensagem específica no
 *   envelope. `exigeRenovacao` trata isso como um 401.
 */

let accessToken: string | null = null;
/** Promise compartilhada do refresh em andamento — o single-flight. */
let refreshEmAndamento: Promise<void> | null = null;

export interface DadosLogin {
  /** Token legado (compat), sempre presente. */
  token: string;
  /** JWT novo — hoje o backend costuma mandar o mesmo valor nos dois campos. */
  accessToken?: string;
  expiresIn?: number;
  emailLogin?: string;
  perfil?: string;
  modulos?: readonly string[];
}

interface LoginResponseBody {
  data: DadosLogin;
  status: { resultado: boolean; mensagem: string };
}

interface RefreshResponseBody {
  data?: { accessToken?: string; token?: string };
  status?: { resultado: boolean; mensagem: string };
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function limparSessao(): void {
  accessToken = null;
}

async function doRefresh(): Promise<void> {
  const resposta = await fetch(`${BASE_TURBOPRO}/Auth/Refresh`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!resposta.ok) {
    throw new HttpError(resposta.status, 'refresh falhou');
  }
  // `accessToken` primeiro: é o único campo que o `/Auth/Refresh` manda
  // (`RefreshResponse` não tem `token`, só o `/Auth/Login` tem). `token` fica
  // como fallback para não quebrar se o backend passar a mandar os dois.
  const corpo = (await resposta.json()) as RefreshResponseBody;
  const token = corpo?.data?.accessToken || corpo?.data?.token;
  if (!token) throw new HttpError(401, 'refresh sem token');
  accessToken = token;
}

function refreshSingleFlight(): Promise<void> {
  if (!refreshEmAndamento) {
    refreshEmAndamento = executarComTrava(doRefresh).finally(() => {
      refreshEmAndamento = null;
    });
  }
  return refreshEmAndamento;
}

/** Renova o JWT sob demanda (usado no boot da sessão, ver `RequireSession`). */
export async function renovarAccessToken(): Promise<string> {
  await refreshSingleFlight();
  if (!accessToken) throw new HttpError(401, 'refresh sem token');
  return accessToken;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  body?: unknown;
  /** Headers extras. Um Authorization aqui não é sobrescrito. */
  headers?: Record<string, string>;
  /** `body` é um FormData pronto (upload) — não faz JSON.stringify nem seta Content-Type. */
  isFormData?: boolean;
  /** Limite desta chamada, em ms; sem ele vale `VITE_API_TIMEOUT` (30 s por padrão). */
  timeoutMs?: number;
}

function montarHeaders(opts: RequestOptions): Headers {
  const headers = new Headers(opts.headers);
  if (!opts.isFormData && !headers.has('Content-Type') && opts.body !== undefined) {
    headers.set('Content-Type', 'application/json');
  }
  if (!headers.has('Authorization') && accessToken) {
    headers.set('Authorization', `Bearer ${accessToken}`);
  }
  return headers;
}

function timeoutConfigurado(): number {
  return Number(env.VITE_API_TIMEOUT) || 30000;
}

async function executar(caminho: string, opts: RequestOptions): Promise<Response> {
  return fetch(`${BASE_TURBOPRO}/${caminho.replace(/^\/+/, '')}`, {
    method: opts.method ?? 'GET',
    credentials: 'include',
    headers: montarHeaders(opts),
    body: opts.isFormData
      ? (opts.body as FormData)
      : opts.body !== undefined
        ? JSON.stringify(opts.body)
        : undefined,
    signal: AbortSignal.timeout(opts.timeoutMs ?? timeoutConfigurado()),
  });
}

/**
 * O legado atrás do gateway não usa o status HTTP como contrato de
 * autenticação: bearer ausente, JWT expirado ou bearer de tipo desconhecido
 * voltam como HTTP 400 com uma mensagem no envelope. Esse 400 também
 * significa "renove o JWT e tente de novo" — exatamente como um 401 nativo.
 *
 * A leitura usa `clone()`: quem chamou continua recebendo o corpo original se
 * não for uma falha de autenticação ou se o refresh falhar.
 */
async function exigeRenovacao(resposta: Response): Promise<boolean> {
  if (resposta.status === 401) return true;
  if (resposta.status !== 400) return false;

  try {
    const corpo = (await resposta.clone().json()) as ApiResponse<unknown>;
    const mensagem = String(corpo?.status?.mensagem ?? '')
      .trim()
      .toLocaleLowerCase('pt-BR');

    return (
      mensagem === 'unauthorized' ||
      mensagem.includes('tipo de autenticação inválida') ||
      mensagem.includes('tipo de autenticacao invalida') ||
      mensagem.includes('token inválido ou expirado') ||
      mensagem.includes('token invalido ou expirado')
    );
  } catch {
    return false;
  }
}

/**
 * Requisição crua com a política de renovação/retry em 401 (ou o dialeto 400
 * equivalente). Prefira os verbos de `api` ou `turboproApi`/`turboproApiLista`
 * (com unwrap) — esta existe para quem precisa do `Response` bruto.
 */
export async function turboproFetch(caminho: string, opts: RequestOptions = {}): Promise<Response> {
  let resposta = await executar(caminho, opts);

  if (!(await exigeRenovacao(resposta))) return resposta;

  // Login, refresh e logout não podem renovar a si mesmos.
  if (
    caminho.endsWith('Auth/Login') ||
    caminho.endsWith('Auth/Refresh') ||
    caminho.endsWith('Auth/Logout')
  ) {
    limparSessao();
    return resposta;
  }

  try {
    await refreshSingleFlight();
  } catch {
    limparSessao();
    anunciarSessaoExpirada();
    return resposta;
  }

  resposta = await executar(caminho, opts);
  return resposta;
}

async function corpoOuErro<T>(resposta: Response): Promise<T> {
  if (!resposta.ok) {
    let mensagem: string | undefined;
    try {
      mensagem = ((await resposta.json()) as ApiResponse<unknown>)?.status?.mensagem;
    } catch {
      /* corpo não-JSON: segue com a mensagem padrão */
    }
    throw new HttpError(resposta.status, mensagem);
  }
  return (await resposta.json()) as T;
}

/**
 * Verbos com a mesma assinatura que `apiIC`/`apiEmalote` já tinham — minimiza
 * o diff nos arquivos `*Api.ts` que só trocam de import.
 */
export const api = {
  get: <T>(endpoint: string, options?: RequestInit) =>
    turboproFetch(endpoint, {
      method: 'GET',
      headers: options?.headers as Record<string, string>,
    }).then(corpoOuErro<T>),

  post: <T, B = unknown>(
    endpoint: string,
    body?: B,
    options?: RequestInit & { timeoutMs?: number }
  ) =>
    turboproFetch(endpoint, {
      method: 'POST',
      body,
      headers: options?.headers as Record<string, string>,
      timeoutMs: options?.timeoutMs,
    }).then(corpoOuErro<T>),

  put: <T, B = unknown>(endpoint: string, body?: B, options?: RequestInit) =>
    turboproFetch(endpoint, {
      method: 'PUT',
      body,
      headers: options?.headers as Record<string, string>,
    }).then(corpoOuErro<T>),

  delete: <T>(endpoint: string, options?: RequestInit) =>
    turboproFetch(endpoint, {
      method: 'DELETE',
      headers: options?.headers as Record<string, string>,
    }).then(corpoOuErro<T>),

  upload: <T>(endpoint: string, formData: FormData) =>
    turboproFetch(endpoint, { method: 'POST', body: formData, isFormData: true }).then(
      corpoOuErro<T>
    ),
};

/** Requisição tipada com envelope: resolve com dado ou lança Business/HttpError. */
export async function turboproApi<T>(
  caminho: string,
  opts: RequestOptions = {}
): Promise<Unwrapped<T>> {
  const resposta = await turboproFetch(caminho, opts);
  return unwrap(await corpoOuErro<ApiResponse<T>>(resposta));
}

/** Igual a `turboproApi`, para endpoints cujo payload é uma LISTA (ver `unwrapLista`). */
export async function turboproApiLista<T>(
  caminho: string,
  opts: RequestOptions = {}
): Promise<Unwrapped<T[] | null>> {
  const resposta = await turboproFetch(caminho, opts);
  return unwrapLista(await corpoOuErro<ApiResponse<T[] | null>>(resposta));
}

/** Autentica e devolve o `data` inteiro do `/Auth/Login`. */
export async function login(usuario: string, senha: string): Promise<DadosLogin> {
  const resposta = await fetch(`${BASE_TURBOPRO}/Auth/Login`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    // Contrato confirmado no Swagger real do ItWorks.Api
    // (api.impostocerto.com.br/turbopro-prod/swagger/v1/swagger.json):
    // `POST /{app}/api/Auth/Login`, `app` é parâmetro de ROTA (enum:
    // impostocerto|impostograma|simulador — "turbopro" nunca foi válido) e o
    // body é só `{ Usuario, Senha }` (LoginRequest tem
    // `additionalProperties: false` — nenhum campo extra, nem header `app`).
    body: JSON.stringify({ Usuario: usuario, Senha: senha }),
  });
  const corpo = (await resposta.json()) as LoginResponseBody;
  if (!resposta.ok || corpo?.status?.resultado === false || !corpo?.data?.token) {
    throw new HttpError(resposta.status, corpo?.status?.mensagem || 'login recusado');
  }
  accessToken = corpo.data.accessToken || corpo.data.token;
  return corpo.data;
}

export interface DadosSelecaoGestor {
  accessToken: string;
  tokenType: string;
  expiresIn: number;
  cnpjGestor: string;
}

/** Valida o vínculo com o gestor e troca o par access/refresh por outro preso a ele. */
export async function selecionarGestor(cnpjGestor: string): Promise<DadosSelecaoGestor> {
  const resposta = await turboproApi<DadosSelecaoGestor>('Auth/SelecionarGestor', {
    method: 'POST',
    body: { CnpjGestor: cnpjGestor },
  });
  accessToken = resposta.data.accessToken;
  return resposta.data;
}

export async function logout(): Promise<void> {
  try {
    await fetch(`${BASE_TURBOPRO}/Auth/Logout`, { method: 'POST', credentials: 'include' });
  } finally {
    limparSessao();
  }
}
